import { Link } from 'react-router-dom';
import { Activity } from '@/services/activityService';
import type { ActivityType } from '@/services/activityTypeService';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, CheckCircle2, ExternalLink, Files, User } from 'lucide-react';
import { formatItalianDate, formatItalianDateTime } from '@/utils/date';
import { cn } from '@/lib/utils';

const statusColors: Record<Activity['status'], string> = {
  PENDING: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  IN_PROGRESS: 'bg-blue-100 text-blue-800 border-blue-300',
  COMPLETED: 'bg-green-100 text-green-800 border-green-300',
  CANCELLED: 'bg-gray-100 text-gray-800 border-gray-300',
};

const statusLabels: Record<Activity['status'], string> = {
  PENDING: 'In Attesa',
  IN_PROGRESS: 'In Corso',
  COMPLETED: 'Completata',
  CANCELLED: 'Annullata',
};

interface ActivityRowReadOnlyProps {
  activity: Activity;
  activityTypes: ActivityType[];
  className?: string;
}

export default function ActivityRowReadOnly({
  activity,
  activityTypes,
  className,
}: ActivityRowReadOnlyProps) {
  const typeMeta = activityTypes.find((t) => t.code === activity.type);
  const typeLabel = typeMeta?.name || activity.type;
  const TypeIcon = Clock;

  const isOverdue =
    activity.dueDate &&
    new Date(activity.dueDate) < new Date() &&
    activity.status !== 'COMPLETED' &&
    activity.status !== 'CANCELLED';
  const daysUntilDue = activity.dueDate
    ? Math.ceil((new Date(activity.dueDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <Card
      className={cn(
        'transition-all hover:shadow-md',
        isOverdue && 'border-red-300 bg-red-50',
        className
      )}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <TypeIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <h3 className="font-semibold text-gray-900 truncate">{activity.title}</h3>
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" asChild>
                <Link to={`/activities/${activity.id}`} title="Apri dettaglio">
                  <ExternalLink className="h-4 w-4" />
                </Link>
              </Button>
            </div>

            <div className="flex items-center gap-2 flex-wrap mb-2">
              {activity.practice?.practiceNumber && (
                <span className="text-xs font-mono text-muted-foreground" title="Numero pratica">
                  #{activity.practice.practiceNumber}
                </span>
              )}
              <Link
                to={`/practices/${activity.practiceId}?tab=documents`}
                className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2 py-0.5 text-xs font-medium text-foreground hover:bg-accent"
                title="Scheda pratica — Documenti"
              >
                <Files className="h-3 w-3" />
                Documenti
              </Link>
            </div>

            {activity.description && (
              <p className="text-sm text-gray-600 line-clamp-2 mb-2">{activity.description}</p>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className={statusColors[activity.status]}>
                {statusLabels[activity.status]}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {typeLabel}
              </Badge>
              {activity.practice && (
                <Badge variant="outline" className="text-xs">
                  {activity.practice.type?.code || 'Pratica'}
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
              {activity.user && (
                <div className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  <span>
                    {activity.user.firstName} {activity.user.lastName}
                  </span>
                </div>
              )}

              {activity.dueDate && (
                <div
                  className={cn('flex items-center gap-1', isOverdue && 'text-red-600 font-semibold')}
                >
                  <Clock className="h-3 w-3" />
                  <span>
                    {isOverdue
                      ? `Scaduta ${Math.abs(daysUntilDue!)} giorni fa`
                      : daysUntilDue === 0
                        ? 'Scade oggi'
                        : daysUntilDue && daysUntilDue > 0
                          ? `Scade tra ${daysUntilDue} giorni`
                          : formatItalianDate(activity.dueDate)}
                  </span>
                </div>
              )}

              {activity.completedAt && (
                <div className="flex items-center gap-1 text-green-600">
                  <CheckCircle2 className="h-3 w-3" />
                  <span>Completata {formatItalianDateTime(activity.completedAt)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
